import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Settings, Wallet, Users, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Loader } from "@/components/shared";
import { AdminPageHeader } from "./adminShared";
import { EarningsEngineCard, BroadcastCard, LeaderboardCloseCard, EmailDeliveryCard } from "./adminExtras";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

export default function AdminSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/settings/public").then((r) => setEmailEnabled(!!r.data.email_enabled)).catch(() => {});
  }, []);
  if (!s) return <Loader />;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setNum = (k) => (e) => setS({ ...s, [k]: parseFloat(e.target.value) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", {
        company_name: s.company_name, trc20_wallet_address: s.trc20_wallet_address,
        bep20_wallet_address: s.bep20_wallet_address, minimum_deposit: s.minimum_deposit,
        maximum_deposit: s.maximum_deposit, referral_percentage: s.referral_percentage,
        minimum_withdrawal: s.minimum_withdrawal ?? 10,
        referral_enabled: s.referral_enabled, platform_status: s.platform_status,
        leaderboard_bonus_enabled: s.leaderboard_bonus_enabled ?? true,
        leaderboard_bonus_1: s.leaderboard_bonus_1 ?? 10, leaderboard_bonus_2: s.leaderboard_bonus_2 ?? 5, leaderboard_bonus_3: s.leaderboard_bonus_3 ?? 2.5,
        withdrawal_requires_2fa: s.withdrawal_requires_2fa ?? false,
      });
      setS(r.data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-400";

  return (
    <div data-testid="admin-settings">
      <AdminPageHeader eyebrow="Configuration" title="Platform Settings"
        right={<button onClick={save} disabled={saving} data-testid="settings-save" className="btn-glow px-6 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes</button>} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Settings className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">General</h3></div>
          <div className="space-y-4">
            <div><label className="text-sm text-slate-300 mb-1.5 block">Company Name</label>
              <input data-testid="settings-company" value={s.company_name} onChange={set("company_name")} className={inputCls} /></div>
            <div><label className="text-sm text-slate-300 mb-1.5 block">Platform Status</label>
              <select data-testid="settings-status" value={s.platform_status} onChange={set("platform_status")} className={inputCls}>
                <option value="active" className="bg-slate-900">Active</option><option value="maintenance" className="bg-slate-900">Maintenance</option></select></div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5"><Wallet className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Deposit</h3></div>
          <div className="space-y-4">
            <div><label className="text-sm text-slate-300 mb-1.5 block">TRC20 Wallet (Tron)</label>
              <input data-testid="settings-trc20" value={s.trc20_wallet_address} onChange={set("trc20_wallet_address")} className={inputCls} /></div>
            <div><label className="text-sm text-slate-300 mb-1.5 block">BEP20 Wallet (BNB)</label>
              <input data-testid="settings-bep20" value={s.bep20_wallet_address} onChange={set("bep20_wallet_address")} className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm text-slate-300 mb-1.5 block">Min Deposit</label><input data-testid="settings-min" type="number" value={s.minimum_deposit} onChange={setNum("minimum_deposit")} className={inputCls} /></div>
              <div><label className="text-sm text-slate-300 mb-1.5 block">Max Deposit</label><input data-testid="settings-max" type="number" value={s.maximum_deposit} onChange={setNum("maximum_deposit")} className={inputCls} /></div>
              <div><label className="text-sm text-slate-300 mb-1.5 block">Min Withdrawal (USDT)</label><input data-testid="settings-min-withdrawal" type="number" value={s.minimum_withdrawal ?? 10} onChange={setNum("minimum_withdrawal")} className={inputCls} /><p className="text-[11px] text-slate-500 mt-1">10% of every withdrawal goes to the Charity Fund (fixed).</p></div>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5"><Users className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Referral</h3></div>
          <div className="grid sm:grid-cols-2 gap-6 items-center">
            <div><label className="text-sm text-slate-300 mb-1.5 block">Direct Referral Percentage (%)</label>
              <input data-testid="settings-refpct" type="number" step="0.1" value={s.referral_percentage} onChange={setNum("referral_percentage")} className={inputCls} /></div>
            <div className="flex items-center gap-3">
              <Switch data-testid="settings-ref-enabled" checked={s.referral_enabled} onCheckedChange={(v) => setS({ ...s, referral_enabled: v })} />
              <span className="text-sm text-slate-300">Referral program {s.referral_enabled ? "enabled" : "disabled"}</span>
            </div>
          </div>
          <div className="border-t border-white/5 mt-6 pt-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div><p className="text-sm text-slate-200">Monthly leaderboard bonus</p><p className="text-xs text-slate-500">Paid automatically to the top 3 referrers when a month closes.</p></div>
              <Switch data-testid="settings-lb-enabled" checked={s.leaderboard_bonus_enabled ?? true} onCheckedChange={(v) => setS({ ...s, leaderboard_bonus_enabled: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((r) => (
                <div key={r}><label className="text-xs text-slate-400 mb-1.5 block">#{r} bonus (USDT)</label>
                  <input data-testid={`settings-lb-bonus-${r}`} type="number" step="0.5" min="0" value={s[`leaderboard_bonus_${r}`] ?? [10, 5, 2.5][r - 1]} onChange={setNum(`leaderboard_bonus_${r}`)} className={inputCls} /></div>
              ))}
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Security</h3></div>
          <div className="flex items-center gap-3">
            <Switch data-testid="settings-withdraw-2fa" checked={s.withdrawal_requires_2fa ?? false} onCheckedChange={(v) => setS({ ...s, withdrawal_requires_2fa: v })} />
            <div><p className="text-sm text-slate-300">Require two-factor authentication for withdrawals</p>
              <p className="text-xs text-slate-500">When off, only users who already enabled 2FA are asked for a code. When on, users must enable 2FA before they can withdraw.</p></div>
          </div>
        </div>

        <ChangePasswordCard className="lg:col-span-2" subtitle={`Signed in as ${user?.email}. This changes the admin console password immediately; you stay logged in on this device.`} />

        <EmailDeliveryCard />

        <EarningsEngineCard />
        <BroadcastCard emailEnabled={emailEnabled} />
        <LeaderboardCloseCard />
      </div>
    </div>
  );
}
